import React, { useRef, useCallback, memo } from "react";
import ReactDOM from "react-dom";
import ReactToPrint from "react-to-print";
import { Button, CardFooter, Input, Row, CustomInput } from "reactstrap";
import Select from "react-select";
import { Colxx } from "../../../components/common/CustomBootstrap";
import ComponentToPrintTem from "../../../containers/pages/ComponentToPrintTem";
import ComponentToPrintNow from "../../../containers/pages/ComponentToPrintNow";
import ComponentToPrintForKitchen from "../../../containers/pages/ComponentToPrintForKitchen";
import {
  AddCommaNumber,
  buildPrintPageStyle,
  buildSilentPrintHtml,
  getStoredPrintPaperWidthMm,
} from "../../../helpers/Utils";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";
import { NotificationManager } from "../../../components/common/react-notifications";

const isElectron = typeof window !== "undefined" && !!window.electronAPI;


const resolvePrintableNode = (target) => {
  if (!target) return null;

  if (target instanceof HTMLElement) {
    return target;
  }

  try {
    const node = ReactDOM.findDOMNode(target);
    return node instanceof HTMLElement ? node : null;
  } catch (error) {
    return null;
  }
};

const FooterOrders = ({
  isOpen,
  intl,
  toggle,
  customer_name,
  customer_phone,
  suggestions = [],
  customerSuggestionField = "customer_phone",
  handleSuggestionClick = () => {},
  onChange,
  handleSubmit,
  handleSubmitChange,
  isSubmitting,
  itemsPending,
  companyName,
  companyAddress,
  companyLogo,
  companyWebsite,
  billDate,
  pin_print,
  discount,
  tax_fee,
  fee_other,
  switchCheckedBill,
  switchCheckedBillBar,
  switchCheckedOrderOne,
  refund_to_customer,
  isChecked,
  customer_debt = 0,
  customer_debt_limit = 0,
  itemsChange,
  removeOrders,
  customer_address,
  save_provisional_when_print,
  print_customer_provisional,
  print_customer_invoice,
  eInvoiceEnabled = false,
  issueEInvoice = false,
  onChangeIssueEInvoice,
  onOpenEInvoiceInfoModal,
  paymentMethod,
  onChangePaymentMethod,
  staffList,
  selectedStaff,
  onChangeStaff,
  isCashierAccount,
  shippingCarrier,
  onChangeShippingCarrier,
  connected_providers = [],
}) => {
  let componentRefTem = useRef(null);
  let componentRef = useRef(null);
  let componentRefProvisional = useRef(null);
  let componentRefKitchen = useRef(null);
  const customerPrintPageStyle = buildPrintPageStyle(
    getStoredPrintPaperWidthMm("customer"),
  );
  const kitchenPrintPageStyle = buildPrintPageStyle(
    getStoredPrintPaperWidthMm("kitchen"),
  );

  const scrollContainerRef = useRef(null);
  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    isDownRef.current = true;
    container.style.cursor = "grabbing";
    startXRef.current = e.pageX - container.offsetLeft;
    scrollLeftRef.current = container.scrollLeft;
  }, []);

  const handleMouseLeaveOrUp = useCallback(() => {
    isDownRef.current = false;
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = "grab";
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDownRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startXRef.current) * 1.5; // scroll speed
    container.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const waitForPrintableResources = useCallback(async (target) => {
    const printable = target?.current || target;

    if (printable && typeof printable.prepareForPrint === "function") {
      try {
        await printable.prepareForPrint();
        return;
      } catch (error) {
        console.log("Prepare invoice resources failed:", error);
      }
    }

    await new Promise((resolve) => {
      setTimeout(resolve, 300);
    });
  }, []);

  const handleBeforeGetContent = useCallback(async () => {
    if (!itemsPending || !itemsPending.length) {
      NotificationManager.warning(
        "Vui lòng chọn sản phẩm để đặt hàng",
        "",
        3000,
        null,
        null,
        "filled"
      );
      return Promise.reject(new Error("Empty cart"));
    }

    const isCreditOrder = isChecked === true || paymentMethod === "credit";
    const hasDebt = isCreditOrder || refund_to_customer > 0;
    const hasCustomerInfo =
      Boolean((customer_name || "").trim()) ||
      Boolean((customer_phone || "").trim()) ||
      Boolean((customer_address || "").trim());

    if (hasDebt && !hasCustomerInfo) {
      NotificationManager.warning(
        "Cần thêm thông tin của khách hàng để ghi nhận công nợ",
        "",
        5000,
        null,
        null,
        "filled"
      );
      return Promise.reject(new Error("Missing customer info for debt"));
    }

    return waitForPrintableResources(componentRef);
  }, [
    waitForPrintableResources,
    itemsPending,
    isChecked,
    paymentMethod,
    refund_to_customer,
    customer_name,
    customer_phone,
    customer_address,
  ]);

  const handleBeforeGetContentProvisional = useCallback(
    () => waitForPrintableResources(componentRefProvisional),
    [waitForPrintableResources],
  );

  const handleAfterPrintProvisional = useCallback(() => {
    if (save_provisional_when_print) {
      return handleSubmit("provisionalInvoice");
    }
    return undefined;
  }, [save_provisional_when_print, handleSubmit]);

  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const printDomNodeSilently = useCallback(async (domNode, documentType = "customer") => {
    if (!domNode || !window.electronAPI?.printSilent) return false;

    await waitForPrintableResources(domNode);

    const printableNode = resolvePrintableNode(domNode);
    if (!printableNode) {
      console.log("Silent print skipped: printable node not found");
      return false;
    }

    const printerName =
      localStorage.getItem("defaultPrinter") ||
      localStorage.getItem("default_printer") ||
      null;

    try {
      const htmlContent = buildSilentPrintHtml(printableNode.outerHTML, {
        paperWidthMm: getStoredPrintPaperWidthMm(documentType),
        documentType,
      });
      const result = await window.electronAPI.printSilent(htmlContent, printerName);

      if (result?.success !== true) {
        console.log("Silent print failed:", result?.error);
        return false;
      }

      return true;
    } catch (error) {
      console.log("Silent print error:", error);
      return false;
    }
  }, [waitForPrintableResources]);

  const handlePaymentSilentPrint = useCallback(
    async (targetRef, documentType = "customer") => {
      if (isSubmitting) return;

      await handleBeforeGetContent();
      if (targetRef?.current) {
        await printDomNodeSilently(targetRef.current, documentType);
      }
      handleSubmit("PAYMENTED");
    },
    [isSubmitting, handleBeforeGetContent, printDomNodeSilently, handleSubmit],
  );

  const handleProvisionalSilentPrint = useCallback(async () => {
    if (isSubmitting) return;

    await handleBeforeGetContentProvisional();
    if (!componentRefProvisional?.current) {
      NotificationManager.error(
        "Không dựng được nội dung hóa đơn tạm tính",
        "Lỗi in",
        5000,
      );
      return;
    }

    const printed = await printDomNodeSilently(
      componentRefProvisional.current,
      "customer",
    );
    if (!printed) {
      NotificationManager.error(
        "Không thể gửi hóa đơn tạm tính tới máy in",
        "Lỗi in",
        5000,
      );
      return;
    }

    if (save_provisional_when_print) {
      await handleSubmit("provisionalInvoice");
    }
  }, [
    isSubmitting,
    handleBeforeGetContentProvisional,
    printDomNodeSilently,
    save_provisional_when_print,
    handleSubmit,
  ]);

  const { messages } = intl;

  const PAYMENT_METHOD_OPTIONS = [
    {
      value: "cash",
      label: messages["home.option.payment.cash"],
      icon: "simple-icon-wallet",
    },
    {
      value: "transfer",
      label: messages["home.option.payment.transfer"],
      icon: "iconsminds-bank",
    },
    {
      value: "card",
      label: messages["home.option.payment.card"],
      icon: "simple-icon-credit-card",
    },
    {
      value: "credit",
      label: messages["home.option.payment.credit"],
      icon: "simple-icon-notebook",
    },
  ];

  const SHIPPING_CARRIER_OPTIONS = [
    {
      value: "ghn",
      label: messages["home.shipping.ghn"],
      icon: "iconsminds-scooter",
    },
    {
      value: "ghtk",
      label: messages["home.shipping.ghtk"],
      icon: "iconsminds-box-close",
    },
    {
      value: "viettelpost",
      label: messages["home.shipping.viettelpost"],
      icon: "iconsminds-post-mail",
    },
    {
      value: "vnpost",
      label: messages["home.shipping.vnpost"],
      icon: "iconsminds-post-mail",
    },
    {
      value: "ahamove",
      label: messages["home.shipping.ahamove"],
      icon: "iconsminds-scooter",
    },
    {
      value: "lalamove",
      label: messages["home.shipping.lalamove"],
      icon: "iconsminds-scooter",
    },
    {
      value: "grabexpress",
      label: messages["home.shipping.grabexpress"],
      icon: "iconsminds-motorcycle",
    },
    {
      value: "shopeexpress",
      label: messages["home.shipping.shopeexpress"],
      icon: "iconsminds-scooter",
    },
    {
      value: "jtexpress",
      label: messages["home.shipping.jtexpress"],
      icon: "iconsminds-scooter",
    },
    {
      value: "ninjavan",
      label: messages["home.shipping.ninjavan"],
      icon: "iconsminds-scooter",
    },
    {
      value: "bestexpress",
      label: messages["home.shipping.bestexpress"],
      icon: "iconsminds-scooter",
    },
    {
      value: "kerryexpress",
      label: messages["home.shipping.kerryexpress"],
      icon: "iconsminds-scooter",
    },
    {
      value: "giaohangtietkiem",
      label: messages["home.shipping.ghtk"],
      icon: "iconsminds-box-close",
    },
  ];

  const formatOptionLabel = ({ label, icon }) => (
    <div className="d-flex align-items-center">
      {icon && <i className={`${icon} mr-2`} style={{ fontSize: "18px" }} />}
      <span>{label}</span>
    </div>
  );

  const timeStart =
    itemsPending && itemsPending.length > 0 ? itemsPending[0].time : null;
  const suggestionContainerStyle =
    customerSuggestionField === "customer_name"
      ? { left: 0, right: "53%" }
      : { left: "50%", right: 0 };

  return (
    <>
      <CardFooter>
        {!itemsChange?.length && (
          <>
            {/* E-Invoice Checkbox - Only show if provider is connected */}
            {eInvoiceEnabled && (
              <div className="d-flex align-items-center justify-content-between mb-2 w-100">
                <CustomInput
                  type="checkbox"
                  id="issue-einvoice-checkbox"
                  checked={issueEInvoice}
                  onChange={(e) =>
                    onChangeIssueEInvoice &&
                    onChangeIssueEInvoice(e.target.checked)
                  }
                  label={
                    <span className="d-flex align-items-center">
                      <IntlMessages id="einvoice.checkbox.issue" />
                    </span>
                  }
                />
                {issueEInvoice && (
                  <Button
                    color="link"
                    size="xs"
                    className="p-0 text-primary"
                    onClick={onOpenEInvoiceInfoModal}
                  >
                    <i className="simple-icon-note mr-1" />
                    Thông tin VAT
                  </Button>
                )}
              </div>
            )}
            <div
              style={{
                borderBottom: "1px solid #e8e8e8",
                paddingBottom: "8px",
                marginBottom: "8px",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center w-100 mt-2 footer-order-selects"
                style={{ gap: "8px" }}
              >
                <div style={{ flex: 1 }}>
                  <Select
                    className="react-select"
                    classNamePrefix="react-select"
                    name="paymentMethod"
                    placeholder={intl.formatMessage({
                      id: "home.placeholder.payment",
                    })}
                    options={PAYMENT_METHOD_OPTIONS}
                    value={
                      PAYMENT_METHOD_OPTIONS.find(
                        (opt) => opt.value === paymentMethod,
                      ) || PAYMENT_METHOD_OPTIONS[0]
                    }
                    onChange={(selected) =>
                      onChangePaymentMethod &&
                      onChangePaymentMethod(selected?.value || "cash")
                    }
                    isSearchable={false}
                    formatOptionLabel={formatOptionLabel}
                    menuPlacement="top"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        border: "none !important",
                        minHeight: "38px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "transparent",
                        },
                        cursor: "pointer",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#145388"
                          : state.isFocused
                            ? "#e6f0f7"
                            : "white",
                        color: state.isSelected ? "white" : "#333",
                        display: "flex !important",
                        alignItems: "center !important",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex !important",
                        alignItems: "center !important",
                      }),
                    }}
                  />
                </div>
                {/* Staff selector */}
                {!isCashierAccount && (
                  <div style={{ flex: 1 }}>
                    <Select
                      className="react-select"
                      classNamePrefix="react-select"
                      name="selectedStaff"
                      placeholder={intl.formatMessage({
                        id: "home.placeholder.staff",
                      })}
                      options={staffList || []}
                      value={
                        (staffList || []).find(
                          (opt) => opt.value === selectedStaff,
                        ) || null
                      }
                      onChange={(selected) =>
                        onChangeStaff && onChangeStaff(selected?.value || null)
                      }
                      isSearchable={true}
                      isClearable={true}
                      formatOptionLabel={({ label }) => label}
                      menuPlacement="top"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "0.5rem",
                          border: "none !important",
                          minHeight: "38px",
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: "transparent",
                          },
                          cursor: "pointer",
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? "#145388"
                            : state.isFocused
                              ? "#e6f0f7"
                              : "white",
                          color: state.isSelected ? "white" : "#333",
                          display: "flex",
                          alignItems: "center",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          display: "flex",
                          alignItems: "center",
                        }),
                      }}
                    />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <Select
                    className="react-select"
                    classNamePrefix="react-select"
                    name="shippingCarrier"
                    placeholder={intl.formatMessage({
                      id: "home.placeholder.shipping",
                    })}
                    options={[
                      {
                        value: null,
                        label: intl.formatMessage({
                          id: "home.option.shipping.select",
                        }),
                        icon: "",
                      },
                      ...SHIPPING_CARRIER_OPTIONS.filter((opt) =>
                        connected_providers.some((p) => p.provider === opt.value),
                      ),
                    ]}
                    value={
                      SHIPPING_CARRIER_OPTIONS.find(
                        (opt) => opt.value === shippingCarrier,
                      ) || {
                        value: null,
                        label: intl.formatMessage({
                          id: "home.option.shipping.select",
                        }),
                        icon: "",
                      }
                    }
                    onChange={(selected) =>
                      onChangeShippingCarrier &&
                      onChangeShippingCarrier(selected?.value || null)
                    }
                    isSearchable={false}
                    formatOptionLabel={formatOptionLabel}
                    menuPlacement="top"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        border: "none !important",
                        minHeight: "38px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "transparent",
                        },
                        cursor: "pointer",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#145388"
                          : state.isFocused
                            ? "#e6f0f7"
                            : "white",
                        color: state.isSelected ? "white" : "#333",
                        display: "flex !important",
                        alignItems: "center !important",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex !important",
                        alignItems: "center !important",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        <div className="position-relative w-100 mt-auto mt-2 mb-3">
          {suggestions.length > 0 && (
            <div className="suggestions-container" style={suggestionContainerStyle}>
              <ul className="suggestions-list">
                {suggestions.map((customer) => (
                  <li
                    key={customer.id || `${customer.name}-${customer.phone}`}
                    onClick={() => {
                      handleSuggestionClick(customer);
                      setTimeout(() => {
                        if (scrollContainerRef.current) {
                          scrollContainerRef.current.scrollTo({
                            left: scrollContainerRef.current.scrollWidth,
                            behavior: "smooth",
                          });
                        }
                      }, 150);
                    }}
                  >
                    <strong>{customer.name}</strong>
                    {customer.phone ? ` - ${customer.phone}` : ""}{" "}
                    {customer.debt > 0
                      ? ` / - ${AddCommaNumber(customer.debt)}`
                      : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeaveOrUp}
            onMouseUp={handleMouseLeaveOrUp}
            onMouseMove={handleMouseMove}
            className="scrollable-customer-info scroll-hide-scrollbar"
            style={{
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              gap: "10px",
              paddingBottom: "4px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              cursor: "grab",
            }}
          >
            <style>{`
              .scroll-hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            <div style={{ width: "47%", flexShrink: 0 }}>
              <Input
                placeholder={intl.formatMessage({
                  id: "home.placeholder.customer-name",
                })}
                onChange={onChange}
                value={customer_name}
                name="customer_name"
                className="border-top-0 border-right-0 border-left-0 rounded-0 p-1"
                autoComplete="off"
                onFocus={(e) =>
                  e.target.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "start",
                  })
                }
              />
            </div>
            <div style={{ width: "43%", flexShrink: 0 }}>
              <Input
                placeholder={intl.formatMessage({ id: "home.placeholder.phone" })}
                onChange={onChange}
                value={customer_phone}
                name="customer_phone"
                className="border-primary border-top-0 border-right-0 border-left-0 rounded-0 p-1"
                autoComplete="off"
                onFocus={(e) =>
                  e.target.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "start",
                  })
                }
              />
            </div>
            <div style={{ width: "65%", flexShrink: 0 }}>
              <Input
                placeholder={
                  intl.formatMessage({ id: "customer.address" }) || "Địa chỉ"
                }
                onChange={onChange}
                value={customer_address}
                name="receiver_address"
                className="border-top-0 border-right-0 border-left-0 rounded-0 p-1"
                autoComplete="off"
                onFocus={(e) =>
                  e.target.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "end",
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Shipping fee display moved to ListOrders */}
        {itemsChange?.length ? (
          <Row className="d-flex justify-content-between align-items-center position-sticky-bottom px-2">
            <div className="w-75" onClick={() => handleSubmitChange()}>
              <Button className="w-100" color="primary">
                <IntlMessages id="home.btn.complete-adjust" />
              </Button>
            </div>
            <div className="w-25 pl-2" onClick={() => removeOrders()}>
              <Button className="w-100" color="danger">
                <IntlMessages id="home.btn.close" />
              </Button>
            </div>
          </Row>
        ) : (
          <Row className="d-flex justify-content-between align-items-center position-sticky-bottom px-2">
            <div
              className="w-65"
              onClick={() => !isSubmitting && handleSubmit("PAYMENTED")}
            >
              {switchCheckedBill ? (
                componentRef && (
                  <div onClick={stopPropagation}>
                    {isElectron ? (
                      <Button
                        className="w-100"
                        color="primary"
                        data-id="btn-payment"
                        disabled={isSubmitting}
                        onClick={() =>
                          handlePaymentSilentPrint(componentRef, "customer")
                        }
                      >
                        {!switchCheckedOrderOne ? (
                          <IntlMessages id="home.btn.order" />
                        ) : itemsChange?.length ? (
                          <IntlMessages id="home.btn.complete-adjust" />
                        ) : (
                          <IntlMessages id="home.btn.payment" />
                        )}
                      </Button>
                    ) : (
                      <ReactToPrint
                        trigger={() => (
                          <Button
                            className="w-100"
                            color="primary"
                            data-id="btn-payment"
                            disabled={isSubmitting}
                          >
                            {!switchCheckedOrderOne ? (
                              <IntlMessages id="home.btn.order" />
                            ) : itemsChange?.length ? (
                              <IntlMessages id="home.btn.complete-adjust" />
                            ) : (
                              <IntlMessages id="home.btn.payment" />
                            )}
                          </Button>
                        )}
                        content={() => componentRef.current}
                        pageStyle={customerPrintPageStyle}
                        onBeforeGetContent={handleBeforeGetContent}
                        onAfterPrint={() => {
                          handleSubmit("PAYMENTED");
                        }}
                      />
                    )}
                  </div>
                )
              ) : !switchCheckedBill && switchCheckedBillBar ? (
                componentRefKitchen && (
                  <div onClick={stopPropagation}>
                    {isElectron ? (
                      <Button
                        className="w-100"
                        color="primary"
                        data-id="btn-payment"
                        disabled={isSubmitting}
                        onClick={() =>
                          handlePaymentSilentPrint(
                            componentRefKitchen,
                            "kitchen",
                          )
                        }
                      >
                        {!switchCheckedOrderOne ? (
                          <IntlMessages id="home.btn.order" />
                        ) : itemsChange?.length ? (
                          <IntlMessages id="home.btn.complete-adjust" />
                        ) : (
                          <IntlMessages id="home.btn.payment" />
                        )}
                      </Button>
                    ) : (
                      <ReactToPrint
                        trigger={() => (
                          <Button
                            className="w-100"
                            color="primary"
                            data-id="btn-payment"
                            disabled={isSubmitting}
                          >
                            {!switchCheckedOrderOne ? (
                              <IntlMessages id="home.btn.order" />
                            ) : itemsChange?.length ? (
                              <IntlMessages id="home.btn.complete-adjust" />
                            ) : (
                              <IntlMessages id="home.btn.payment" />
                            )}
                          </Button>
                        )}
                        content={() => componentRefKitchen.current}
                        pageStyle={kitchenPrintPageStyle}
                        onBeforeGetContent={handleBeforeGetContent}
                        onAfterPrint={() => {
                          handleSubmit("PAYMENTED");
                        }}
                      />
                    )}
                  </div>
                )
              ) : (
                <Button
                  className="w-100"
                  color="primary"
                  data-id="btn-payment"
                  disabled={isSubmitting}
                >
                  {!switchCheckedOrderOne ? (
                    <IntlMessages id="home.btn.order" />
                  ) : itemsChange?.length ? (
                    <IntlMessages id="home.btn.complete-adjust" />
                  ) : (
                    <IntlMessages id="home.btn.payment" />
                  )}
                </Button>
              )}
            </div>
            <div className="w-35 pl-2">
              {itemsPending?.length > 0 &&
                (isElectron ? (
                  <div onClick={stopPropagation}>
                    <Button
                      color="primary"
                      className="w-100"
                      data-id="btn-print-provisional"
                      disabled={isSubmitting}
                      onClick={handleProvisionalSilentPrint}
                    >
                      <IntlMessages id="home.btn.print-provisional" />
                    </Button>
                  </div>
                ) : (
                  <ReactToPrint
                    trigger={() => (
                      <Button
                        color="primary"
                        className="w-100"
                        data-id="btn-print-provisional"
                        disabled={isSubmitting}
                      >
                        <IntlMessages id="home.btn.print-provisional" />
                      </Button>
                    )}
                    content={() => componentRefProvisional.current}
                    pageStyle={customerPrintPageStyle}
                    onBeforeGetContent={handleBeforeGetContentProvisional}
                    onAfterPrint={handleAfterPrintProvisional}
                  ></ReactToPrint>
                ))}
            </div>
            <div style={{ display: "none" }}>
              {itemsPending.map((item, idx) => {
                return (
                  <ComponentToPrintTem
                    key={idx}
                    item={item}
                    ref={(el) => (componentRefTem.current = el)}
                    style={{ display: "none" }}
                  />
                );
              })}
            </div>
            <div style={{ display: "none" }}>
              <ComponentToPrintNow
                items={itemsPending}
                companyName={companyName}
                companyAddress={companyAddress}
                companyLogo={companyLogo}
                companyWebsite={companyWebsite}
                billDate={billDate}
                pin_print={pin_print}
                discount={discount}
                tax_fee={tax_fee}
                fee_other={fee_other}
                timeStart={timeStart}
                customer_name={customer_name}
                customer_phone={customer_phone}
                customer_address={customer_address}
                print_customer={print_customer_invoice}
                mask_phone={true}
                paymentMethod={paymentMethod}
                customer_debt={customer_debt}
                refund_to_customer={refund_to_customer}
                isChecked={isChecked}
                ref={componentRef}
                style={{ display: "none" }}
              />
            </div>
            <div style={{ display: "none" }}>
              <ComponentToPrintNow
                items={itemsPending}
                companyName={companyName}
                companyAddress={companyAddress}
                companyLogo={companyLogo}
                companyWebsite={companyWebsite}
                billDate={billDate}
                pin_print="HĐTT*"
                footerNote="*HĐTT không có giá trị sử dụng, bạn hãy yêu cầu nhân viên in hóa đơn bán hàng trước khi thanh toán"
                discount={discount}
                tax_fee={tax_fee}
                fee_other={fee_other}
                timeStart={timeStart}
                customer_name={customer_name}
                customer_phone={customer_phone}
                customer_address={customer_address}
                print_customer={print_customer_provisional}
                paymentMethod={paymentMethod}
                customer_debt={customer_debt}
                refund_to_customer={refund_to_customer}
                isChecked={isChecked}
                show_variant_badges
                ref={componentRefProvisional}
                style={{ display: "none" }}
              />
            </div>
            <div style={{ display: "none" }}>
              <ComponentToPrintForKitchen
                items={itemsPending}
                billDate={billDate}
                pin_print={pin_print}
                discount={discount}
                ref={componentRefKitchen}
                style={{ display: "none" }}
              />
            </div>
          </Row>
        )}
      </CardFooter>
    </>
  );
};

export default injectIntl(memo(FooterOrders));
